import { useCallback } from 'react'
import { useAuthStore } from '../store/authStore.js'

export interface UploadResult {
  attachmentId: string
  filename: string
  mimeType: string
  sizeBytes: number
}

export interface UseUploadAttachmentReturn {
  upload: (file: File) => Promise<UploadResult>
}

/**
 * Uploads a single file as multipart/form-data to the attachments endpoint.
 *
 * The shared `api` client (api/client.ts) only speaks JSON, so multipart
 * uploads use raw `fetch` here per the plan (Task 5). Auth comes from the
 * zustand auth store, matching the client's Bearer scheme.
 */
export function useUploadAttachment(): UseUploadAttachmentReturn {
  const upload = useCallback(async (file: File): Promise<UploadResult> => {
    const form = new FormData()
    form.append('file', file)
    const csrfToken = useAuthStore.getState().csrfToken ?? ''
    console.log('[useUploadAttachment] Starting upload', { fileName: file.name, fileSize: file.size, fileType: file.type, csrfTokenPresent: Boolean(csrfToken) })
    const res = await fetch('/api/attachments/upload', {
      method: 'POST',
      headers: { 'X-CSRF-Token': csrfToken },
      body: form,
      credentials: 'include',
    })
    console.log('[useUploadAttachment] Response received', { status: res.status, statusText: res.statusText, ok: res.ok })
    if (!res.ok) {
      const body = (await res
        .json()
        .catch(() => ({ error: 'UPLOAD_FAILED' }))) as { error?: string }
      console.error('[useUploadAttachment] Upload failed', { status: res.status, error: body.error })
      throw new Error(body.error ?? `Upload failed (${res.status})`)
    }
    const result = (await res.json()) as UploadResult
    console.log('[useUploadAttachment] Upload success', result)
    return result
  }, [])
  return { upload }
}
